// src/pages/Center Admin/LabReportsEntry.tsx
import React, { useEffect, useState } from "react";
import "../../center admin css/labPickup.css";
import "../../center admin css/centerAdmin.css";
import LabReportsEntryActivePatientCard from "../../components/Center Admin/Reuse/LabReportsEntryActivePatientCard";
import LabReportForm from "../../components/Center Admin/forms/LabReportForm";
import { SidebarItem } from "../../components/Center Admin/layout/CenterAppointmentsQueue";
import LabReportQueue from "../../components/Center Admin/Reuse/LabReportQueue";

import api from "../../api/axiosConfig";

const LabReportsEntry: React.FC = () => {
  const selectedCenter = sessionStorage.getItem("centerId") || "";
  const selectedDate = sessionStorage.getItem("selectedDate") || "";

  // ✅ Global patient sync (like FormsPage & BnuSamplePage)
  const [patient, setPatient] = useState<SidebarItem | null>(null);
  const [queueData, setQueueData] = useState<any>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [refreshQueueKey, setRefreshQueueKey] = useState(0);

  const storageKey = "selectedPatient_LAB_REPORTS_Q";

  const handleSubmitSuccess = () => {
    sessionStorage.removeItem(storageKey);

    setPatient(null);

    setRefreshQueueKey((prev) => prev + 1);

    window.dispatchEvent(new Event("patientUpdated"));
  };

  const loadPatient = () => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed: SidebarItem = JSON.parse(stored);
        setPatient((prev: SidebarItem | null) =>
          JSON.stringify(prev) !== JSON.stringify(parsed) ? parsed : prev,
        );
      } else {
        setPatient(null);
      }
    } catch {
      setPatient(null);
    }
  };

  // ✅ Load on mount
  useEffect(() => {
    loadPatient();

    const fetchQueue = async () => {
      try {
        setQueueLoading(true);

        // ✅ READ CENTER + DATE
        const centerId = sessionStorage.getItem("centerId");
        const selectedDate = sessionStorage.getItem("selectedDate");

        if (!centerId || !selectedDate) {
          console.error("[LabReportsEntry] Missing centerId or selectedDate");
          return;
        }

        console.log("[LabReportsEntry] Fetch Queue:", centerId, selectedDate);

        // ✅ API CALL
        const response = await api.centerworkflowApi.get(
          `/queues/B_AND_U_BLOOD_COLLECTED_Q`,
          {
            params: {
              centerId,
              date: selectedDate,
            },
          },
        );

        console.log("[LabReportsEntry] Queue Response:", response.data);

        // setQueueData(response.data);
        setQueueData(response.data);

        const waitingPatients = response.data?.waiting || [];

        const storedPatient = sessionStorage.getItem(storageKey);

        if (storedPatient) {
          const parsedPatient = JSON.parse(storedPatient);

          const stillExists = waitingPatients.some(
            (p: any) => p.sessionId === parsedPatient.sessionId,
          );

          // ✅ Old selected patient completed → auto move next patient
          if (!stillExists) {
            if (waitingPatients.length > 0) {
              const nextPatient = {
                emrId: waitingPatients[0]?.child?.childId,
                patientName: waitingPatients[0]?.child?.name,
                age: waitingPatients[0]?.child?.age,
                profile: waitingPatients[0]?.child?.profile,
                sessionId: waitingPatients[0]?.sessionId,
              };

              sessionStorage.setItem(storageKey, JSON.stringify(nextPatient));

              setPatient(nextPatient as any);

              window.dispatchEvent(new Event("patientUpdated"));
            } else {
              sessionStorage.removeItem(storageKey);
              setPatient(null);
            }
          }
        }
      } catch (error) {
        console.error("[LabReportsEntry] Failed to fetch queue:", error);
      } finally {
        setQueueLoading(false);
      }
    };

    fetchQueue();
  }, []);

  // ✅ Sync when patient is updated globally
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey) loadPatient();
    };
    const handleCustomUpdate = () => loadPatient();

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("patientUpdated", handleCustomUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("patientUpdated", handleCustomUpdate);
    };
  }, []);

  return (
    <div className="center-admin-container">
      {/* ===== LEFT QUEUE SIDEBAR ===== */}
      <LabReportQueue
        key={refreshQueueKey}
        centerId={selectedCenter}
        selectedDate={selectedDate}
        queueApi="B_AND_U_BLOOD_COLLECTED_Q"
        onSelect={(selectedPatient) => {
          sessionStorage.setItem(
            "selectedPatient_LAB_REPORTS_Q",
            JSON.stringify(selectedPatient),
          );

          // ✅ SAVE SESSION ID
          const patientWithSession = selectedPatient as any;

          if (patientWithSession?.sessionId) {
            sessionStorage.setItem(
              "workflowSessionId",
              patientWithSession.sessionId,
            );
          }

          window.dispatchEvent(new Event("patientUpdated"));
        }}
      />

      {/* ===== RIGHT CONTENT AREA ===== */}
      <div className="center-content">
        {/* ===== Active Patient Info Card ===== */}
        <LabReportsEntryActivePatientCard patient={patient || undefined} />

        {/* ===== Lab Report Form ===== */}
        <div className="lab-reports-content">
          {patient?.emrId && (
            <div key={patient.emrId}>
              <LabReportForm
                emrId={patient.emrId}
                onSuccess={handleSubmitSuccess}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LabReportsEntry;
